import { useMemo } from 'react'

import { Space, Spacer, Text } from '@procurify/ui'

import { type TimezonesEnum } from '@webapp/core/entities/external/enums/TimezonesEnum'
import { useProcurifyIntl, useFeatureFlag } from '@webapp/hooks'
import messages from './messages'
import { BudgetCategoryCard } from '../BudgetCategoryCard'
import { SpendTrackerDepartmentCardStyled } from './styles/SpendTrackerDepartmentCardStyled'

export interface ISpendTrackerDepartmentCardProps {
  name: string
  startDate: Nullable<Date>
  endDate: Nullable<Date>
  currentSpend: string | number
  currentRequest: string | number
  otherPendingRequests: string | number
  spendAfterApproval: string | number
  timeZoneTooltip: TimezonesEnum

  committedSpend: number
  requestedSpend?: number
  totalSpendBeforePending: number
  pendingSpend: number
  totalSpend: number
}

export const SpendTrackerDepartmentCard = ({
  name,
  startDate,
  endDate,
  currentSpend,
  currentRequest,
  otherPendingRequests,
  spendAfterApproval,
  timeZoneTooltip,

  committedSpend,
  requestedSpend,
  totalSpendBeforePending,
  pendingSpend,
  totalSpend,
}: ISpendTrackerDepartmentCardProps) => {
  const intl = useProcurifyIntl()
  const FT_5817_ENABLE_BUDGETS_REDESIGN = useFeatureFlag(
    'FT_5817_ENABLE_BUDGETS_REDESIGN'
  )

  const renderCommittedTooltip = useMemo(() => {
    return (
      <Space>
        <Space.Item>
          <Text align='left'>
            {intl.formatMessage(messages.committedTooltipLine1)}
          </Text>
        </Space.Item>
        <Space.Item>
          <Text align='left'>
            {intl.formatMessage(messages.committedTooltipLine2)}
          </Text>
        </Space.Item>
      </Space>
    )
  }, [])

  if (FT_5817_ENABLE_BUDGETS_REDESIGN) {
    return (
      <SpendTrackerDepartmentCardStyled>
        <BudgetCategoryCard>
          <Space size='medium' justify='space-around'>
            <BudgetCategoryCard.Header
              name={name}
              nameTooltip={intl.formatMessage(messages.headerToolTip)}
              startDate={startDate}
              endDate={endDate}
              timeZoneTooltip={timeZoneTooltip}
            />
            <Space size='xsmall'>
              <BudgetCategoryCard.Subtotal
                label={intl.formatMessage(messages.committedLabel)}
                labelTooltip={renderCommittedTooltip}
                amount={committedSpend}
                shouldHidePercentage
              />
              {Boolean(requestedSpend) && (
                <BudgetCategoryCard.Subtotal
                  label={intl.formatMessage(messages.requestedSpendLabel)}
                  amount={requestedSpend}
                  shouldHidePercentage
                />
              )}

              <Space.Divider />
              <BudgetCategoryCard.Subtotal
                label={intl.formatMessage(
                  messages.totalSpendBeforePendingSpendLabel
                )}
                amount={totalSpendBeforePending}
                isTotal
                shouldHidePercentage
              />

              <Spacer />
              <BudgetCategoryCard.Subtotal
                label={intl.formatMessage(messages.pendingSpendLabel)}
                amount={pendingSpend}
                shouldHidePercentage
              />

              <Space.Divider />
              <BudgetCategoryCard.Subtotal
                label={intl.formatMessage(messages.totalSpendLabel)}
                amount={totalSpend}
                isTotal
                shouldHidePercentage
              />
            </Space>
          </Space>
        </BudgetCategoryCard>
      </SpendTrackerDepartmentCardStyled>
    )
  }

  // remove with FT_5817_ENABLE_BUDGETS_REDESIGN
  return (
    <SpendTrackerDepartmentCardStyled>
      <BudgetCategoryCard>
        <BudgetCategoryCard.Header
          name={name}
          startDate={startDate}
          endDate={endDate}
          timeZoneTooltip={timeZoneTooltip}
        />
        <BudgetCategoryCard.DetailsBreakdown>
          <BudgetCategoryCard.DetailsBreakdown.Row
            name={intl.formatMessage(messages.currentSpend)}
            amount={currentSpend}
          />
          <BudgetCategoryCard.DetailsBreakdown.Row
            name={intl.formatMessage(messages.currentRequest)}
            amount={currentRequest}
          />
          <BudgetCategoryCard.DetailsBreakdown.Row
            name={intl.formatMessage(messages.spendAfterApproval)}
            amount={spendAfterApproval}
            highlight
          />
        </BudgetCategoryCard.DetailsBreakdown>
        <BudgetCategoryCard.GranularSpend
          ariaLabel={intl.formatMessage(messages.granularSpendTableLabel)}
        >
          <BudgetCategoryCard.GranularSpend.Row
            name={intl.formatMessage(messages.otherPendingRequests)}
            amount={otherPendingRequests}
          />
        </BudgetCategoryCard.GranularSpend>
      </BudgetCategoryCard>
    </SpendTrackerDepartmentCardStyled>
  )
}

SpendTrackerDepartmentCard.displayName = 'SpendTrackerDepartmentCard'
